<template>
  <div>

    <!-- ORIGIN ADDRESS -->

    <div class="columns dp-flex">
      <div
        v-if="data.topic === 'originAddressLineOne'"
        class="column"
      >
        <b-field id="origin-address">
          <b-input
            v-validate="'required'"
            v-model="form.originAddressLineOne"
            :placeholder="$t('kyc.address_placeholder')"
            name="origin address"
            autocomplete="disabled"
            expanded
            data-test="edit-kyc-address--origin-address-field"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin address') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div
        v-if="data.topic === 'originAddressLineTwo'"
        class="column"
      >
        <b-field>
          <b-input
            v-model="form.originAddressLineTwo"
            :placeholder="$t('kyc.address_placeholder')"
            autocomplete="disabled"
            expanded
            data-test="edit-kyc-address--origin-address-line-2-field"
          />
        </b-field>
      </div>
    </div>
    <div class="columns dp-flex">
      <div
        v-if="data.topic === 'originCountry'"
        class="column"
      >
        <b-field
          id="origin-country"
          class="kyc-address-is-expanded"
        >
          <b-select
            v-validate="'required'"
            v-model="form.originCountry"
            :placeholder="$t('kyc.country')"
            name="origin country"
            data-test="edit-kyc-address--origin-country-field"
          >
            <option
              v-for="(country, index) in countries"
              :value="country.en_short_name"
              :key="index"
              :disabled="country.disabled"
            >
              {{ country.en_short_name }}
            </option>
          </b-select>
        </b-field>
        <span class="is-invalid">{{ errors.first('origin country') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div
        v-if="data.topic === 'originProvince'"
        class="column"
      >
        <b-field id="origin-province">
          <b-select
            v-validate="'required'"
            v-if="form.originCountry ? form.originCountry=== 'Thailand' : originCountry === 'Thailand'"
            v-model="form.originProvince"
            :placeholder="$t('kyc.province_placeholder')"
            name="origin province"
            expanded
            data-test="edit-kyc-address--origin-province-field"
          >
            <option
              v-for="province in provincesInThailand"
              :value="province.key"
              :key="province.key"
            >
              {{ province.th }}
            </option>
          </b-select>
          <b-input
            v-validate="'required'"
            v-else
            v-model="form.originProvince"
            :placeholder="$t('kyc.province_placeholder')"
            name="origin province"
            autocomplete="disabled"
            expanded
            data-test="edit-kyc-address--origin-province-field"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin province') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div
        v-if="data.topic === 'originCity'"
        class="column"
      >
        <b-field id="origin-city">
          <b-input
            v-validate="'required'"
            v-model="form.originCity"
            :placeholder="$t('kyc.city_placeholder')"
            name="origin city"
            autocomplete="disabled"
            expanded
            data-test="edit-kyc-address--origin-city-field"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin city') }}</span>
      </div>
      <div
        v-if="data.topic === 'originPostcode'"
        class="column"
      >
        <b-field id="origin-postcode">
          <b-input
            v-validate="(form.originCountry && form.originCountry === 'Thailand') || originCountry === 'Thailand' ? 'required|numeric|length:5' : 'required'"
            v-model="form.originPostcode"
            :placeholder="$t('kyc.zipcode')"
            name="origin postcode"
            autocomplete="disabled"
            expanded
            data-test="edit-kyc-address--origin-postcode-field"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin postcode') }}</span>
      </div>
    </div>

    <!-- CURRENT ADDRESS  -->

    <div v-show="!isCurrentAddressSameOrigin">
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentAddressLineOne'"
          class="column"
        >
          <b-field id="current-address">
            <b-input
              v-validate="'required'"
              v-model="form.currentAddressLineOne"
              :placeholder="$t('kyc.address_placeholder')"
              name="current address"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-address-field"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current address') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentAddressLineTwo'"
          class="column"
        >
          <b-field>
            <b-input
              v-model="form.currentAddressLineTwo"
              :placeholder="$t('kyc.address_placeholder')"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-address-line-2-field"
            />
          </b-field>
        </div>
      </div>
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentCountry'"
          class="column"
        >
          <b-field
            id="current-country"
            class="kyc-address-is-expanded"
          >
            <b-select
              v-validate="'required'"
              v-model="form.currentCountry"
              :placeholder="$t('kyc.country')"
              name="current country"
              data-test="edit-kyc-address--current-country-field"
            >
              <option
                v-for="(country, index) in countries"
                :value="country.en_short_name"
                :key="index"
                :disabled="country.disabled"
              >
                {{ country.en_short_name }}
              </option>
            </b-select>
          </b-field>
          <span class="is-invalid">{{ errors.first('current country') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentProvince'"
          class="column"
        >
          <b-field id="current-province">
            <b-select
              v-validate="'required'"
              v-if="form.currentCountry ? form.currentCountry === 'Thailand' : currentCountry === 'Thailand'"
              v-model="form.currentProvince"
              :placeholder="$t('kyc.province_placeholder')"
              name="current province"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-province-field"
            >
              <option
                v-for="province in provincesInThailand"
                :value="province.key"
                :key="province.key"
              >
                {{ province.th }}
              </option>
            </b-select>
            <b-input
              v-validate="'required'"
              v-else
              v-model="form.currentProvince"
              :placeholder="$t('kyc.province_placeholder')"
              name="current province"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-province-field"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current province') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentCity'"
          class="column"
        >
          <b-field id="current-city">
            <b-input
              v-validate="'required'"
              v-model="form.currentCity"
              :placeholder="$t('kyc.city_placeholder')"
              name="current city"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-city-field"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current city') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div
          v-if="data.topic === 'currentPostcode'"
          class="column"
        >
          <b-field id="current-postcode">
            <b-input
              v-validate="(form.currentCountry && form.currentCountry === 'Thailand') || currentCountry === 'Thailand' ? 'required|numeric|length:5' : 'required'"
              v-model="form.currentPostcode"
              :placeholder="$t('kyc.zipcode')"
              name="current postcode"
              autocomplete="disabled"
              expanded
              data-test="edit-kyc-address--current-postcode-field"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current postcode') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Address',
  inject: ['$validator'],
  props: {
    form: {
      type: Object,
      default: () => {}
    },
    originCountry: {
      type: String,
      required: true
    },
    currentCountry: {
      type: String,
      required: true
    },
    countries: {
      type: Array,
      default: () => []
    },
    provincesInThailand: {
      type: Array,
      default: () => []
    },
    data: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      isCurrentAddressSameOrigin: false
    }
  },
  computed: {
    originAddressLineOne () {
      return this.form.originAddressLineOne
    },
    originAddressLineTwo () {
      return this.form.originAddressLineTwo
    },
    originProvince () {
      return this.form.originProvince
    },
    originCity () {
      return this.form.origincity
    },
    originPostcode () {
      return this.form.originPostcode
    },
    computedForm: function () {
      return Object.assign({}, this.form)
    }
  },
  watch: {
    isCurrentAddressSameOrigin (value) {
      if (value) {
        this.form.currentAddressLineOne = this.form.originAddressLineOne
        this.form.currentAddressLineTwo = this.form.originAddressLineTwo
        this.form.currentProvince = this.form.originProvince
        this.form.currentCity = this.form.originCity
        this.form.currentPostcode = this.form.originPostcode
      } else {
        this.form.currentAddressLineOne = ''
        this.form.currentAddressLineTwo = ''
        this.form.currentProvince = ''
        this.form.currentCity = ''
        this.form.currentPostcode = ''
      }
    },
    originAddressLineOne (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentAddressLineOne = value
      }
    },
    originAddressLineTwo (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentAddressLineTwo = value
      }
    },
    originProvince (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentProvince = value
      }
    },
    originCity (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentCity = value
      }
    },
    originPostcode (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentPostcode = value
      }
    },
    computedForm: {
      handler: function (newVal, oldVal) {
        if (newVal.originCountry !== oldVal.originCountry) {
          delete this.form.originProvince
        }
        if (newVal.currentCountry !== oldVal.currentCountry) {
          delete this.form.currentProvince
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
.kyc-address-is-expanded {
  .control {
    .select {
      width: 100%;
      select {
        width: 100%;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.dp-flex {
  display: flex;
}
</style>



// WEBPACK FOOTER //
// src/pages/SummaryOfKYC/components/Address.vue